/**
 * Resolve a template view's CASE PHOTO to BYTES, for the classic (no-bake) render path.
 *
 * The synced wire is pixel-free by contract (Constitution VII): a view carries
 * `base_case_image_ref`, never the photo. The engine therefore had the reference and no way to
 * turn it into pixels, and a classic export was the artwork on a bare canvas. Resolving the ref
 * is the client's job — the same division of labour the baked path already uses — and this is
 * where it happens.
 *
 * Precedence, deliberately the SAME chain `TemplateEditor` displays (one rule in the codebase,
 * not two):
 *   1. the view's published `base_case_image_ref` — authoritative, and the only source another
 *      device has;
 *   2. this device's local image store (`stores/imageStore.ts`) — the authoring device's own
 *      copy, encrypted at rest on desktop, IndexedDB on web. PRIMARY view only: the store is
 *      keyed by template, and what it holds is the back-view product photo.
 *
 * Hosted bytes go through `bakedSync`'s download ladder, so a case photo and a baked asset fail
 * the same way and retry the same way: status checked (an R2 XML error document is never stored
 * as an image), empty 200s retried, 404/403 answered once, desktop pulling through Rust.
 *
 * Results are memoized per `templateId:versionId:view:ref` for the process. A run renders N designs
 * against ONE product; re-downloading multi-MB photography per job is exactly the cost the baked
 * path's store exists to avoid.
 *
 * The VERSION id is in that key on purpose. The ref alone is not enough: swapping a template's
 * reference photo goes through Save, which writes a new version, and an upload that reuses the
 * same object URL would otherwise be served the previous photo from this memo for the rest of the
 * session — a stale product in every export, with nothing on screen to explain it. The memo is
 * also dropped wholesale on sign-out ({@link clearCaseImageCache}, wired into
 * `app/signOut.ts::purgeContent`): it holds the previous account's product photography.
 */
import type { EngineBinding } from "@genz/engine-bind";
import type { PlatformIO } from "@genz/platform";
import { fetchBytesWithRetry } from "../templates/bakedSync.js";
import { PRIMARY_VIEW } from "../templates/views.js";
import { loadTemplateImageBytes } from "./imageStore.js";

export interface ResolveCaseImageDeps {
  /** WS-W: on desktop the bytes come from Rust, on web from `fetch`. Absent ⇒ `fetch`. */
  downloadBytes?: PlatformIO["downloadBytes"];
  /** Reported instead of thrown: a case photo that will not load must not fail the run. The
   *  render then falls back to today's design-only output, which is what it did before. */
  onWarn?: (message: string) => void;
}

const cache = new Map<string, Uint8Array>();

/** Only http(s) refs are fetchable. A `blob://…` / `asset://…` ref names a local or unrebased
 *  object and is not something this device can GET — the local store answers those or nothing
 *  does. */
function isFetchable(ref: string): boolean {
  return /^https?:\/\//i.test(ref);
}

/**
 * The case photo for `view`, or `null` when this device has none.
 *
 * `null` is a legitimate answer, not a failure: a template with no photo anywhere renders exactly
 * as it always did. Never throws.
 */
export async function resolveCaseImageBytes(
  engine: EngineBinding,
  templateId: string,
  versionId: string,
  view: string,
  ref: string | null | undefined,
  deps: ResolveCaseImageDeps = {},
): Promise<Uint8Array | null> {
  const key = `${templateId}:${versionId}:${view}:${ref ?? ""}`;
  const hit = cache.get(key);
  if (hit) return hit;

  if (ref && isFetchable(ref)) {
    try {
      const bytes = await fetchBytesWithRetry(ref, deps.downloadBytes);
      if (bytes.length > 0) {
        cache.set(key, bytes);
        return bytes;
      }
    } catch (e) {
      // Named, then fall through to the local copy — which on the authoring device is the same
      // photo and costs nothing. A warn, never a throw: the remedy is a re-upload, and blocking
      // the whole run on it would be worse than the design-only render this replaces.
      deps.onWarn?.(
        `case photo for view "${view}" could not be downloaded (${e instanceof Error ? e.message : String(e)})`,
      );
    }
  }

  // The local store holds the PRIMARY view's product photo, keyed by template id alone.
  if (view === PRIMARY_VIEW) {
    const local = await loadTemplateImageBytes(engine, templateId);
    if (local && local.length > 0) {
      cache.set(key, local);
      return local;
    }
  }
  return null;
}

/** Drop the memo. Called by the sign-out CONTENT purge (`app/signOut.ts`) — the bytes it holds
 *  are the previous account's product photography, which must not survive into the next
 *  session — and available to any caller that needs a forced re-resolve. */
export function clearCaseImageCache(): void {
  cache.clear();
}
